import isNil from 'lodash/isNil'
import {ElaAssignmentTypes} from '@freckle/educator-entities/ts/ela/assignments/ela-assignment'
import {MathAssignmentTypes} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import {patchSubjectProductAssignment} from '@freckle/classroom/ts/api/assignment'
import type {SelectedCourseToStudentIdsMapT} from '@freckle/classroom/ts/common/logic/student-course-picker'
import type {AssignmentSubmitParamsT} from '@freckle/classroom/ts/ela/adaptive-skills/components/assign-skill-practice/assign-skill-form-container'
import {createAssignmentPromise} from '@freckle/classroom/ts/ela/adaptive-skills/logic/assignment'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import type {MultiCourseAssignResponse, MultiCourseAssignmentPostT} from './assignments'
import {getMultiCourseAssignmentPromise} from './assignments'

type AssignmentParams =
  MathAssignmentParams | ElaGrammarOrPassageAssignmentParams | ElaWordStudyAssignmentParams

type MathAssignmentParams = {
  tag: 'math'
  assignmentDataMap: MultiCourseAssignmentPostT
}

export type ElaGrammarOrPassageAssignmentParams = {
  tag: 'ela-grammar-or-passage'
  selectedStudents: SelectedCourseToStudentIdsMapT
  assignmentParams: AssignmentSubmitParamsT
}

export type ElaWordStudyAssignmentParams = {
  tag: 'ela-word-study'
  assignmentDataMap: MultiCourseAssignmentPostT
}

export async function tryAsStudent(
  contentArea: 'math' | 'ela',
  teacher: TeacherAttrs,
  assignmentParams: AssignmentParams,
  loginUrl: string,
  markClaimedFreeResourcesStale: () => Promise<void>,
  title?: string
) {
  if (contentArea === 'ela' && isNil(teacher.elaStandardSetId)) return

  const res =
    assignmentParams.tag === 'math' || assignmentParams.tag === 'ela-word-study'
      ? await getMultiCourseAssignmentPromise(
          contentArea === 'math'
            ? MathAssignmentTypes.MathStandard
            : ElaAssignmentTypes.ElaWordStudy,
          {math: teacher.mathStandardSetId, ela: teacher.elaStandardSetId},
          assignmentParams.assignmentDataMap,
          markClaimedFreeResourcesStale
        )
      : await createAssignmentPromise(
          assignmentParams.selectedStudents,
          assignmentParams.assignmentParams,
          undefined,
          teacher.elaStandardSetId!
        )

  const assignmentId = (res as MultiCourseAssignResponse)[0]!.assignmentId

  if (title !== undefined) {
    await patchSubjectProductAssignment(
      {subjectPath: contentArea === 'math' ? 'math' : 'ela', productPath: 'targeted'},
      assignmentId,
      {title}
    )
  }

  const redirect = redirectLink(assignmentParams.tag, loginUrl, assignmentId)
  if (redirect === undefined) return
  window.open(redirect, '_blank', 'noopener')
}

function redirectLink(tag: string, loginUrl: string, assignmentId: number) {
  if (tag === 'math') return `${loginUrl}&redirect=/assignments/targeted/${assignmentId}/session`
  if (tag === 'ela-word-study')
    return `${loginUrl}&redirect=/ela/assignments/word-study/${assignmentId}`
  if (tag === 'ela-grammar-or-passage')
    return `${loginUrl}&redirect=/ela/system/targeted-skills-practice/assignments/${assignmentId}`
  return undefined
}
